import * as tslib_1 from "tslib";
import { HttpClient } from '@angular/common/http';
import { UsersApi } from '@api/users/users.api';
import { environment } from '@environment';
import { Apollo } from 'apollo-angular';
import { chain, isArray } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExpiredPasswordError } from './exceptions/expired-password-error.exception';
import { ExpiredPasswordSoonError } from './exceptions/expired-password-soon-error.exception';
import { NewPasswordRequiredError } from './exceptions/password-required-error.exception';
import { SoftwareTokenMfaRequiredError } from './exceptions/software-token-mfa-qequired-error.exception';
import { UnauthorizedUserError } from './exceptions/unauthorized-user-error.exception';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../api/users/users.api";
import * as i3 from "apollo-angular";
var AuthenticationService = /** @class */ (function () {
    function AuthenticationService(http, usersApi, apolo) {
        this.http = http;
        this.usersApi = usersApi;
        this.apolo = apolo;
        this.state = new BehaviorSubject(null);
        this.user$ = this.state
            .asObservable()
            .pipe(map(function (state) { return state && state.user; }));
    }
    AuthenticationService.prototype.getMe = function (_a) {
        var accessToken = _a.accessToken, updateState = _a.updateState;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var token, _b, me, accountPreferences, permissions, isAdminOrAll, hasSomePermission;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        token = accessToken || this.accessToken;
                        return [4 /*yield*/, this.usersApi.meWithAccessToken(token)];
                    case 1:
                        _b = _c.sent(), me = _b.me, accountPreferences = _b.accountPreferences;
                        permissions = chain((me && me.groups) || [])
                            .map(function (group) { return group.permissions; })
                            .flatten()
                            .map(function (permission) { return permission.name; })
                            .uniq()
                            .toArray()
                            .value();
                        isAdminOrAll = me && ['admin', 'all'].includes(me.type);
                        hasSomePermission = permissions.length > 0;
                        if (!(!isAdminOrAll || !hasSomePermission)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.logout()];
                    case 2:
                        _c.sent();
                        throw new UnauthorizedUserError();
                    case 3:
                        if (updateState) {
                            this.state.next({
                                user: me,
                                permissions: permissions,
                                accountPreferences: accountPreferences,
                                idToken: this.idToken,
                                accessToken: this.accessToken,
                            });
                        }
                        return [2 /*return*/, { me: me, accountPreferences: accountPreferences, permissions: permissions }];
                }
            });
        });
    };
    Object.defineProperty(AuthenticationService.prototype, "user", {
        get: function () {
            return this.state.value && this.state.value.user;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthenticationService.prototype, "accessToken", {
        get: function () {
            return this.state.value && this.state.value.accessToken;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthenticationService.prototype, "idToken", {
        get: function () {
            return this.state.value && this.state.value.idToken;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthenticationService.prototype, "permissions", {
        get: function () {
            return (this.state.value && this.state.value.permissions) || [];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthenticationService.prototype, "preferences", {
        get: function () {
            return (this.state.value && this.state.value.accountPreferences) || {};
        },
        enumerable: true,
        configurable: true
    });
    AuthenticationService.prototype.getEndpoint = function (provider) {
        if (provider === 'email-password') {
            return 'sign-in';
        }
        if (provider === 'active-directory') {
            return 'sign-in-with-ad';
        }
        if (provider === 'google') {
            return 'sign-in-with-google';
        }
        return null;
    };
    AuthenticationService.prototype.login = function (_a) {
        var email = _a.email, adIdToken = _a.adIdToken, password = _a.password, provider = _a.provider;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var endpoint, body, url, response;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getEndpoint(provider)];
                    case 1:
                        endpoint = _b.sent();
                        body = provider === 'email-password' ? { email: email, password: password } : { idToken: adIdToken };
                        url = environment.MORANNON_URL + "/auth/" + endpoint;
                        return [4 /*yield*/, this.http.post(url, body).toPromise()];
                    case 2:
                        response = _b.sent();
                        if (response.session) {
                            this.session = response.session;
                        }
                        if (response.resetPasswordSession) {
                            this.resetPasswordSession = response.resetPasswordSession;
                        }
                        if (response.passwordStatus && response.passwordStatus.isPasswordExpired) {
                            throw new ExpiredPasswordError();
                        }
                        if (response.passwordStatus && response.passwordStatus.isPasswordSoonExpired) {
                            throw new ExpiredPasswordSoonError(response);
                        }
                        if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
                            throw new NewPasswordRequiredError();
                        }
                        if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
                            throw new SoftwareTokenMfaRequiredError();
                        }
                        return [2 /*return*/, this.updateStateAndSetRefreshToken(response)];
                }
            });
        });
    };
    AuthenticationService.prototype.loginWithSoftwareToken = function (_a) {
        var email = _a.email, code = _a.code;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url, body, response;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        url = environment.MORANNON_URL + "/auth/respond-challenge";
                        body = {
                            session: this.session,
                            challengeName: 'SOFTWARE_TOKEN_MFA',
                            challengeResponses: {
                                SOFTWARE_TOKEN_MFA_CODE: code,
                                USERNAME: email
                            }
                        };
                        return [4 /*yield*/, this.http.post(url, body).toPromise()];
                    case 1:
                        response = _b.sent();
                        return [2 /*return*/, this.updateStateAndSetRefreshToken(response)];
                }
            });
        });
    };
    AuthenticationService.prototype.loginWithRecoveryCode = function (_a) {
        var email = _a.email, password = _a.password, recoveryCode = _a.recoveryCode;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url, body, response;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        url = environment.MORANNON_URL + "/auth/sign-in-with-recovery-code";
                        body = { email: email, password: password, recoveryCode: recoveryCode };
                        return [4 /*yield*/, this.http.post(url, body).toPromise()];
                    case 1:
                        response = _b.sent();
                        return [2 /*return*/, this.updateStateAndSetRefreshToken(response)];
                }
            });
        });
    };
    AuthenticationService.prototype.logout = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var accessToken, ignored_1, ignored_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        accessToken = this.accessToken;
                        if (!accessToken) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.http.post(environment.MORANNON_URL + "/auth/logout", { accessToken: accessToken }).toPromise()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        ignored_1 = _a.sent();
                        return [3 /*break*/, 4];
                    case 4:
                        this.state.next(null);
                        localStorage.removeItem('REFRESH_TOKEN');
                        localStorage.removeItem('ACCESS_TOKEN');
                        this.stopRefreshTokenTimer();
                        _a.label = 5;
                    case 5:
                        _a.trys.push([5, 7, , 8]);
                        return [4 /*yield*/, this.apolo.getClient().cache.reset()];
                    case 6:
                        _a.sent();
                        return [3 /*break*/, 8];
                    case 7:
                        ignored_2 = _a.sent();
                        return [3 /*break*/, 8];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.refreshTokenAndPermissions = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url, refreshToken, response, meResponse;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        url = environment.MORANNON_URL + "/auth/refresh-token";
                        refreshToken = localStorage.getItem('REFRESH_TOKEN');
                        if (!refreshToken) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, this.http.post(url, { refreshToken: refreshToken })
                                .toPromise()
                                .catch(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                return tslib_1.__generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0: return [4 /*yield*/, this.logout()];
                                        case 1:
                                            _a.sent();
                                            return [2 /*return*/, null];
                                    }
                                });
                            }); })];
                    case 1:
                        response = _a.sent();
                        if (!response) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, this.getMe({
                                accessToken: response.accessToken,
                                updateState: false
                            }).catch(function () { return null; })];
                    case 2:
                        meResponse = _a.sent();
                        if (!meResponse) {
                            return [2 /*return*/];
                        }
                        this.state.next({
                            user: meResponse.me,
                            permissions: meResponse.permissions,
                            accountPreferences: meResponse.accountPreferences,
                            idToken: response.idToken,
                            accessToken: response.accessToken,
                        });
                        localStorage.setItem('ACCESS_TOKEN', response.accessToken);
                        this.startRefreshTokenTimer();
                        return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.startRefreshTokenTimer = function () {
        var _this = this;
        var oneHour = 60 * 60 * 1000;
        clearTimeout(this.refreshTokenTimeoutId);
        this.refreshTokenTimeoutId = setTimeout(function () { return _this.refreshTokenAndPermissions(); }, oneHour);
    };
    AuthenticationService.prototype.stopRefreshTokenTimer = function () {
        clearTimeout(this.refreshTokenTimeoutId);
    };
    AuthenticationService.prototype.updateStateAndSetRefreshToken = function (response) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, user, accountPreferences, permissions;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getMe({ accessToken: response.accessToken, updateState: false })];
                    case 1:
                        _a = _b.sent(), user = _a.me, accountPreferences = _a.accountPreferences, permissions = _a.permissions;
                        this.state.next({
                            user: user,
                            permissions: permissions,
                            accountPreferences: accountPreferences,
                            idToken: response.idToken,
                            accessToken: response.accessToken,
                        });
                        localStorage.setItem('REFRESH_TOKEN', response.refreshToken);
                        localStorage.setItem('ACCESS_TOKEN', response.accessToken);
                        this.startRefreshTokenTimer();
                        return [2 /*return*/, user];
                }
            });
        });
    };
    AuthenticationService.prototype.createNewPassword = function (_a) {
        var email = _a.email, newPassword = _a.newPassword;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.http
                            .post(environment.MORANNON_URL + '/auth/confirm-registration', {
                            email: email,
                            newPassword: newPassword,
                            session: this.session
                        })
                            .toPromise()];
                    case 1:
                        response = _b.sent();
                        if (response.session) {
                            this.session = response.session;
                        }
                        if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
                            throw new SoftwareTokenMfaRequiredError();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.createNewPasswordAfterExpiredAndLogin = function (newPassword) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.http
                            .post(environment.MORANNON_URL + '/auth/post-auth-sign-in-with-new-password', {
                            newPassword: newPassword,
                            resetPasswordSession: this.resetPasswordSession
                        })
                            .toPromise()];
                    case 1:
                        response = _a.sent();
                        if (response.session) {
                            this.session = response.session;
                        }
                        if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
                            throw new SoftwareTokenMfaRequiredError();
                        }
                        return [2 /*return*/, response];
                }
            });
        });
    };
    AuthenticationService.prototype.sendPasswordRecoveryEmail = function (email) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        url = environment.MORANNON_URL + "/auth/forgot-password";
                        return [4 /*yield*/, this.http.post(url, { email: email }).toPromise()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.confirmCodeAndCreateNewPassword = function (_a) {
        var email = _a.email, verificationCode = _a.verificationCode, newPassword = _a.newPassword;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url, response;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        url = environment.MORANNON_URL + "/auth/confirm-password";
                        return [4 /*yield*/, this.http
                                .post(url, {
                                email: email,
                                verificationCode: verificationCode,
                                newPassword: newPassword
                            })
                                .toPromise()];
                    case 1:
                        response = _b.sent();
                        if (response.session) {
                            this.session = response.session;
                        }
                        if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
                            throw new SoftwareTokenMfaRequiredError();
                        }
                        return [2 /*return*/, response];
                }
            });
        });
    };
    AuthenticationService.prototype.hasPermission = function (permissionName) {
        return this.permissions.includes(permissionName);
    };
    AuthenticationService.prototype.hasFlags = function (flags) {
        if (!this.user || !this.user.account || !this.user.account.id) {
            return false;
        }
        return this.user.account.featureFlags.some(function (flag) {
            return flag.active && (isArray(flags) ? flags.includes(flag.name) : flag.name === flags);
        });
    };
    AuthenticationService.prototype.hasAccountPreference = function (_a) {
        var property = _a.property, subProperty = _a.subProperty, expectedValue = _a.expectedValue, defaultIfUndefined = _a.defaultIfUndefined;
        var expectedIsDefaultValue = defaultIfUndefined === expectedValue;
        var accountPreferences = this.state.value.accountPreferences;
        if (!accountPreferences || !accountPreferences[property] || accountPreferences[property][subProperty] === null) {
            return expectedIsDefaultValue;
        }
        return accountPreferences[property][subProperty] === expectedValue;
    };
    AuthenticationService.prototype.getAccountPreference = function (_a) {
        var property = _a.property, subProperty = _a.subProperty, defaultIfNull = _a.defaultIfNull;
        var accountPreferences = this.state.value && this.state.value.accountPreferences;
        if (!accountPreferences || !accountPreferences[property] || accountPreferences[property][subProperty] === null) {
            return defaultIfNull;
        }
        return accountPreferences[property][subProperty];
    };
    AuthenticationService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function AuthenticationService_Factory() { return new AuthenticationService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.UsersApi), i0.ɵɵinject(i3.Apollo)); }, token: AuthenticationService, providedIn: "root" });
    return AuthenticationService;
}());
export { AuthenticationService };
